import React, { useCallback, useEffect, useState } from "react";
import SuiBox from "components/SuiBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Button, Card, Icon, Tooltip } from "@mui/material";
import Searchbar from "containers/Searchbar";
import { TailSpin } from "react-loader-spinner";
import Table from "examples/Tables/Table";
import TemplateSearch from "./subComponents/TemplateSearch";
import { userSelector } from "store/features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import NoData from "containers/DataNotAvailBox";
import { templateRows } from "./subComponents/TemplateRows";
import { updateFirstLetter } from "utils/helper";
import SuiButton from "components/SuiButton";
import ModalRoot from "containers/modal/ModalRoot";
import AddTemplate from "./subComponents/AddTemplate";
import DeleteModal from "containers/DeleteModal";
import { deleteDietChartTemplate } from "store/features/authSlice";
import SuiTypography from "components/SuiTypography";
import { removeTopLevelEmptyKeys } from "utils/common";

const Templates = () => {
  const dispatch = useDispatch();
  const { dietChartTemplates, isFetching, uid } = useSelector(userSelector);
  const [rows, setRows] = useState([]);
  const [filterQuery, setFilterQuery] = useState({
    toDate: null,
    fromDate: null,
    chartType: null,
    weekPlan: null,
  });
  const [searchVal, setsearchVal] = useState("");
  const [dietChartTemplateNames, setDietChartTemplateNames] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templateArray, setTemplateArray] = useState([]);
  const [openAddTemplateDrawer, setOpenAddTemplateDrawer] = useState(false);
  const [mode, setMode] = useState();
  const [selectedItem, setSelectedItem] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);
  useEffect(() => {
    if (dietChartTemplates) {
      const sortedTemplates = [...dietChartTemplates].sort((a, b) => {
        const isTimestamp = (d) =>
          d &&
          typeof d.seconds === "number" &&
          typeof d.nanoseconds === "number";

        const aIsTimestamp = isTimestamp(a.createdAt);
        const bIsTimestamp = isTimestamp(b.createdAt);

        if (!aIsTimestamp && !bIsTimestamp) return 0;
        if (!aIsTimestamp) return 1;
        if (!bIsTimestamp) return -1;

        const aDate =
          a.createdAt.seconds * 1000 + a.createdAt.nanoseconds / 1000000;
        const bDate =
          b.createdAt.seconds * 1000 + b.createdAt.nanoseconds / 1000000;

        return bDate - aDate;
      });

      const templateNames = sortedTemplates.map((template) => ({
        label: template.templateName,
        value: {
          dietPlan: template.dietPlan,
          chartType: template.chartType,
          weekPlan: template.weekPlan,
          note: template.note,
        },
      }));
      setDietChartTemplateNames(templateNames);

      const allTemplates = sortedTemplates.map((template) => ({
        ...template,
        name: updateFirstLetter(template.templateName),
        dietPlan: template.dietPlan,
        chartType: template.chartType,
        weekPlan: template.weekPlan,
        note: template.note,
      }));
      setAllTemplates(allTemplates);
    }
  }, [dietChartTemplates]);

  const updateHandler = (item) => {
    const dietplan = removeTopLevelEmptyKeys(item.dietPlan);

    setSelectedItem({
      ...item,
      weekPlan: item.weekPlan,
      chartType: item.chartType,
      notes: item.note,
      ...dietplan,
    });
    setMode("edit");
  };
  const onConfirmDeleteHandler = () => {
    dispatch(deleteDietChartTemplate({ templateName: selectedItem.name, uid }));
    setDeleteModal(false);
  };

  const viewHandler = (item) => {
    const dietplan = removeTopLevelEmptyKeys(item.dietPlan);

    setSelectedItem({
      ...item,
      weekPlan: item.weekPlan,
      chartType: item.chartType,
      notes: item.note,
      ...dietplan,
    });
    setMode("view");
  };

  const deleteHandler = useCallback(
    (item) => {
      setSelectedItem(item);
      setDeleteModal(true);
    },
    [setDeleteModal, setSelectedItem]
  );

  useEffect(() => {
    setRows(
      templateRows(templateArray, viewHandler, updateHandler, deleteHandler)
    );
  }, [templateArray]);

  const columns = [
    { name: "S.no", align: "left" },
    { name: "Date/Time (Added)", align: "left" },
    { name: "Name", align: "left" },
    { name: "Chart Type", align: "left" },
    { name: "Week Plan", align: "left" },
    { name: "Notes", align: "left" },
    { name: "Actions", align: "left" },
  ];

  const filterHandler = () => {
    const filtered = allTemplates.filter((item) => {
      let match = true;
      const { fromDate, toDate, chartType, weekPlan } = filterQuery;

      const normalize = (val) => {
        if (!val) return "";
        if (typeof val === "string") return val.trim().toLowerCase();
        if (typeof val === "object")
          return val.value?.trim().toLowerCase() || "";
        return "";
      };

      const itemChartType = normalize(item.chartType);
      const itemWeekPlan = normalize(item.weekPlan);
      const filterChartType = normalize(chartType);
      const filterWeekPlan = normalize(weekPlan);

      if (filterChartType && itemChartType !== filterChartType) match = false;

      if (filterWeekPlan && itemWeekPlan !== filterWeekPlan) match = false;

      if (item?.createdAt?.seconds) {
        const createdDate = new Date(item.createdAt.seconds * 1000);

        if (fromDate) {
          const from = new Date(fromDate);
          from.setHours(0, 0, 0, 0);
          if (createdDate < from) match = false;
        }

        if (toDate) {
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          if (createdDate > to) match = false;
        }
      }

      if (
        searchVal &&
        !item?.name?.toLowerCase()?.includes(searchVal.toLowerCase())
      ) {
        match = false;
      }

      return match;
    });

    setTemplateArray(filtered);
  };

  const resetHandler = useCallback(() => {
    setFilterQuery({
      toDate: null,
      fromDate: null,
      chartType: null,
      weekPlan: null,
    });
    setTemplateArray(allTemplates);
  }, [allTemplates]);

  useEffect(() => {
    if (!searchVal) {
      resetHandler();
      setTemplateArray(allTemplates);
    } else {
      resetHandler();
      let filteredClients = allTemplates.filter((s) => {
        let name = s?.name;
        return name.toLowerCase().includes(searchVal.toLowerCase());
      });
      setTemplateArray(filteredClients);
    }
  }, [isFetching, allTemplates, searchVal, resetHandler]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox mb={3}>
        <Card style={{ overflow: "inherit" }}>
          <SuiBox
            pt={4}
            px={3}
            className="search-box-div"
            style={{
              width: "100%",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <SuiTypography display="flex" alignItems="center">
              <SuiTypography variant="h6">{"Templates List"}</SuiTypography>
              <span
                style={{
                  fontSize: "14px",
                  height: "16px",
                  width: "14px",
                }}
              >
                <Tooltip
                  title={
                    "Below are the list of templates you have created, make sure it is updated"
                  }
                  placement="top"
                  style={{ color: "#67748e", height: "16px", width: "14px" }}
                >
                  <Icon color="inherit">{"help"}</Icon>
                </Tooltip>
              </span>
            </SuiTypography>
            <SuiBox display="flex">
              <Searchbar
                placeholder={"Search template Name"}
                searchVal={searchVal}
                searchHandler={(e) => {
                  setsearchVal((_) => e);
                }}
                style_={{ marginRight: 25, marginBottom: 10 }}
                clearHandler={() => {
                  setsearchVal("");
                }}
              />

              <SuiButton
                component={Button}
                color="dark"
                variant="gradient"
                onClick={() => setMode("create")}
              >
                Add Template
              </SuiButton>
            </SuiBox>
          </SuiBox>

          <TemplateSearch
            setFilterQuery={setFilterQuery}
            filterHandler={filterHandler}
            filterQuery={filterQuery}
            resetHandler={resetHandler}
          />

          <SuiBox
            sx={{
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": {
                  borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                    `${borderWidth[1]} solid ${borderColor}`,
                },
              },
            }}
          >
            {isFetching ? (
              <SuiBox
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 30,
                }}
              >
                <TailSpin color="#17c1e8" height={60} width={60} />
              </SuiBox>
            ) : rows.length ? (
              <Table columns={columns} rows={rows} maxRows={10} />
            ) : (
              <NoData />
            )}
          </SuiBox>
        </Card>
      </SuiBox>

      <ModalRoot
        variant="permanent"
        ownerState={{ openConfigurator: mode }}
        width="90%"
        style={{ opacity: "0 !important" }}
      >
        <AddTemplate
          open={mode}
          setOpenAddTemplateDrawer={setOpenAddTemplateDrawer}
          mode={mode}
          setMode={setMode}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
        />
      </ModalRoot>

      {deleteModal && (
        <DeleteModal
          isFetching={isFetching}
          onDeleteHandler={onConfirmDeleteHandler}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
        />
      )}
    </DashboardLayout>
  );
};

export default Templates;
